import React, { useEffect } from 'react';
import { FormInputSelect } from 'components/form';
import { formatCurrency } from 'utils';
import { ProductConfig, ProposalProductCardModes } from '../../../../../types';
import { BuckeyeConstructorPlan, BuckeyeConstructorTerm, BuckeyePlan, BuckeyeTerm } from './types';

interface Props {
  mode: ProposalProductCardModes;
  plans: Array<BuckeyePlan | BuckeyeConstructorPlan>;
  configuration: ProductConfig;
  handlePlanChange: (index: number) => void;
  handleTermChange: (term: BuckeyeTerm) => void;
  handleTermChangeConstructor: (term: BuckeyeConstructorTerm) => void;
  handleDeductibleChange: (deductibleId: string) => void;
}

export const Fields = ({
  mode,
  plans,
  configuration,
  handlePlanChange,
  handleTermChange,
  handleTermChangeConstructor,
  handleDeductibleChange,
}: Props) => {
  const isConstructor = mode === ProposalProductCardModes.Constructor;

  const effectivePlanIndex =
    configuration.buckeyePlanIndex != null && plans[configuration.buckeyePlanIndex] != null
      ? configuration.buckeyePlanIndex
      : configuration.subName != null
        ? plans.findIndex(p => p.name === configuration.subName)
        : -1;

  const selectedPlan = effectivePlanIndex >= 0 ? plans[effectivePlanIndex] : null;

  const selectedTerm: BuckeyeTerm | null = configuration.coverage ?? null;
  const sortedTerms: Array<BuckeyeTerm | BuckeyeConstructorTerm> = selectedPlan
    ? [...selectedPlan.terms].sort((a, b) => a.months - b.months)
    : [];

  useEffect(() => {
    if (plans?.length === 1 && configuration.buckeyePlanIndex == null) {
      handlePlanChange(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  useEffect(() => {
    if (isConstructor) return;
    if (selectedTerm?.deductibles?.length === 1 && !configuration.buckeyeDeductibleId) {
      handleDeductibleChange(selectedTerm.deductibles[0].deductibleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  const getTermValue = (): string => {
    if (isConstructor) {
      if (configuration.buckeyeConstructorTermMonths == null) return '';
      const idx = sortedTerms.findIndex(
        t =>
          t.months === configuration.buckeyeConstructorTermMonths &&
          (t.miles ?? null) === (configuration.buckeyeConstructorTermMiles ?? null)
      );
      return idx >= 0 ? idx.toString() : '';
    }
    if (!selectedTerm) return '';
    const idx = sortedTerms.findIndex(t => (t as BuckeyeTerm).rateId === selectedTerm.rateId);
    return idx >= 0 ? idx.toString() : '';
  };

  const getTermLabel = (term: BuckeyeTerm | BuckeyeConstructorTerm): string => {
    const base = `${term.months} Months${term.miles ? ` / ${term.miles.toLocaleString()} Miles` : ''}`;
    if (isConstructor) return base;
    return `${base} \u2014 ${formatCurrency((term as BuckeyeTerm).pricing.retailAmount)}`;
  };

  return (
    <>
      <FormInputSelect
        variant="standard"
        label="Plan"
        onChange={(value: string) => handlePlanChange(parseInt(value, 10))}
        value={effectivePlanIndex >= 0 ? effectivePlanIndex.toString() : ''}
        options={[
          { label: 'Select Plan', value: '' },
          ...plans.map((plan, index) => ({
            label: plan.name,
            value: index.toString(),
          })),
        ]}
      />

      {selectedPlan && (
        <FormInputSelect
          variant="standard"
          label="Term"
          onChange={(value: string) => {
            const term = sortedTerms[parseInt(value, 10)];
            if (!term) return;
            if (isConstructor) {
              handleTermChangeConstructor(term as BuckeyeConstructorTerm);
            } else {
              handleTermChange(term as BuckeyeTerm);
            }
          }}
          value={getTermValue()}
          options={[
            { label: 'Select Term', value: '' },
            ...sortedTerms.map((term, index) => ({
              label: getTermLabel(term),
              value: index.toString(),
            })),
          ]}
        />
      )}

      {!isConstructor && selectedTerm && selectedTerm.deductibles.length > 0 && (
        <FormInputSelect
          variant="standard"
          label="Deductible"
          onChange={handleDeductibleChange}
          value={configuration.buckeyeDeductibleId ?? ''}
          options={[
            { label: 'Select Deductible', value: '' },
            ...selectedTerm.deductibles.map(deductible => ({
              label: deductible.description,
              value: deductible.deductibleId,
            })),
          ]}
        />
      )}
    </>
  );
};
